import { Highlight } from "@mantine/core";
import * as _ from "lodash-es";
import type { MRT_ColumnDef, MRT_TableOptions } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import type { HistoryDateBoundedValue } from "../../../../utils/backend-types";
import { sortBackendDates } from "../../../../utils/dates/sortBackendDates";

const DISPLAY_NO_DATE_LIMIT = "-";
const MIN_DATE_VALUE = "01.01.0001";
const MAX_DATE_VALUE = "31.12.9999";

export function DateBoundedValuesTable({
  values,
  highlight = ""
}: {
  values: Array<HistoryDateBoundedValue>;
  highlight?: string;
}) {
  const columns = useMemo<Array<MRT_ColumnDef<HistoryDateBoundedValue>>>(
    () => [
      {
        accessorFn: (row) => dateColumnAccessor(row.first_date),
        id: "first_date",
        header: "Von",
        size: 10,
        enableColumnFilter: false,
        sortingFn: (rowA, rowB) =>
          sortBackendDates(
            rowA.original.first_date ?? undefined,
            rowB.original.first_date ?? undefined
          )
      },
      {
        accessorFn: (row) => dateColumnAccessor(row.last_date),
        id: "last_date",
        header: "Bis",
        size: 10,
        enableColumnFilter: false,
        sortingFn: (rowA, rowB) =>
          sortBackendDates(
            rowA.original.last_date ?? undefined,
            rowB.original.last_date ?? undefined
          )
      },
      {
        accessorFn: (row) => {
          if (row.value === true) {
            return "Ja";
          } else if (row.value === false) {
            return "Nein";
          } else {
            return row.value;
          }
        },
        Cell: ({ cell }) => (
          <Highlight highlight={highlight}>{String(cell.getValue())}</Highlight>
        ),
        id: "value",
        header: "",
        enableColumnFilter: false
      }
    ],
    [highlight]
  );

  const enablePagination = values.length > 5;

  const defaultOptions = getDefaultMRTOptions<HistoryDateBoundedValue>({
    emptyRowsFallbackText: "Leer",
    initialState: {
      pagination: { pageIndex: 0, pageSize: 5 }
    },
    hideBorder: true
  });

  const tableOptions = _.merge({}, defaultOptions, {
    enableTopToolbar: false,
    enableToolbarInternalActions: false,
    enableColumnFilters: false,
    enablePagination: enablePagination,
    enableSorting: false,
    enableBottomToolbar: enablePagination,
    mantineTableProps: {
      withColumnBorders: true,
      style: {
        tableLayout: "fixed"
      }
    },
    mantineTableBodyCellProps: {
      style: {
        whiteSpace: "normal"
      }
    }
  }) satisfies Partial<MRT_TableOptions<HistoryDateBoundedValue>>;

  const table = useMantineReactTable({
    ...tableOptions,
    columns,
    data: values
  });

  return <MantineReactTable table={table} />;
}

function dateColumnAccessor(backendDate: string | null) {
  if (
    backendDate === null ||
    backendDate === MIN_DATE_VALUE ||
    backendDate === MAX_DATE_VALUE
  ) {
    return DISPLAY_NO_DATE_LIMIT;
  }

  return backendDate;
}
